import { Icon } from '@fv/client-components'

import { HideForVendor } from '@/features/auth/HasRole'
import type { ShipmentContentProps } from '@/features/shipment-details-panel/types'
import { ShipmentTagsControl } from '@/features/tags/ShipmentTagsControl'
import { DetailedTrackingHistory } from '@/features/track/DetailedTrackingHistory'
import { ShipmentTrackingBar } from '@/features/track/ShipmentTrackingBar'
import { useCarrierPublicSettings } from '@/hooks/useCarrierPublicSettings'
import { getCarrierDetails } from '@/utils/shipmentFuncs'

import { InputAdornment } from '../inputs/InputGroup'
import { ShipperTooltip } from '../ShipperTooltip'
import { DeliveryDate } from './DeliveryDate'
import { NotificationsBanner } from './NotificationsBanner'
import { RouteDetails } from './RouteDetails'

export const DetailsPanel = ({
  load,
  setActiveSlider,
}: ShipmentContentProps) => {
  const { selectedQuote } = load
  const { carrierPublicSettings } = useCarrierPublicSettings(
    selectedQuote?.carrierId,
  )
  const carrierDetails = getCarrierDetails(load)

  return (
    <div className="flex min-h-0 flex-col flex-nowrap items-stretch overflow-hidden b1350:row-start-2">
      <NotificationsBanner
        carrierSettings={carrierPublicSettings}
        load={load}
        setActiveSlider={setActiveSlider}
      />
      <div className="bg-fv-beer-xlight px-6 py-4 border-b border-fv-beer-medium">
        <div className="flex items-start gap-4">
          {carrierDetails && (
            <span className="whitespace-nowrap rounded-xs border border-fv-beer-medium bg-fv-beer-medium/50 px-2 py-1">
              <Icon icon="truck" className="text-fv-gray-dark" />
              <span>{carrierDetails}</span>
            </span>
          )}
          <p className="mb-0 mt-[.2rem] flex items-start overflow-hidden whitespace-nowrap">
            <DeliveryDate load={load} />
          </p>

          {carrierPublicSettings?.dispatchPhone && (
            <a
              className="mt-[.2rem] whitespace-nowrap border-l border-l-[#dbd59e] pl-4 leading-[1.4rem]"
              href={`tel:${carrierPublicSettings.dispatchPhone}`}
            >
              <ShipperTooltip label={carrierPublicSettings.dispatchPhone}>
                <Icon icon="phone" />
              </ShipperTooltip>
              <span className="b1450:hidden b1350:inline b1100:hidden">
                {carrierPublicSettings.dispatchPhone}
              </span>
            </a>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-6 pb-6 pt-0">
        <ShipmentTrackingBar
          load={load}
          onClick={() => setActiveSlider('track')}
        />
        <HideForVendor>
          <div className="flex mb-4">
            <InputAdornment
              label="Tag this shipment"
              position="start"
              className="whitespace-nowrap"
            />
            <ShipmentTagsControl loadId={load.loadId} />
          </div>
        </HideForVendor>
        <RouteDetails holdAtLocation={load.holdAtLocation} load={load} />
        <DetailedTrackingHistory loadId={load.loadId} />
      </div>
    </div>
  )
}
